import { useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { useForm } from 'react-hook-form'
import { parseUnits } from 'viem'

import { SwapRequest } from '~/components/forms/swap/SwapFormSection'
import {
  getSwapQuoteKey,
  useReverseSwapQuoteQuery,
  useSwapQuoteQuery,
  useSwapRequest,
} from '~/components/forms/swap/hooks/useSwap'
import { SwapDirection } from '~/components/forms/swap/utils'
import { useTransferResolverContext } from '~/components/forms/transfer/hooks/useTransferResolverContext'
import {
  getValidatedRecipients,
  transferFormResolver,
  TransferFormValues,
  TransferResolverContext,
} from '~/components/forms/transfer/transferSchema'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useCreateTransferTransaction } from '~/hooks/smart-account-actions/useCreateTransferTransaction'
import { useCreateSwapTransaction } from '~/hooks/smart-account-actions/useSmartAccountActions'
import { useMultipleSmartAccountBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useTokenFeatures } from '~/hooks/useTokenFeatures'
import { TokenMetadata, useTokenMetadata } from '~/hooks/useTokenMetadata'
import {
  BackendLoggingData,
  EarnAction,
  IAddress,
  OrgSmartAccount,
  OrgTransaction,
  TransactionRecipient,
} from '~/types'
import { isTransactChainId } from '~/utils'
import { Address, EvmAddress } from '~/utils/address'
import { bpsToPercentage, DEFAULT_SLIPPAGE_BPS } from '~/utils/swap'
import { isVaultToken } from '~/utils/vault'

export const useTransferForm = ({
  defaultSender,
  defaultRecipients,
  defaultTransferToken,
  defaultMemo,
  transactionId,
}: {
  defaultSender?: IAddress
  defaultRecipients?: TransactionRecipient[]
  defaultTransferToken?: {
    token?: IAddress
    amount?: string
    chainId?: SupportedChainId
  }
  defaultMemo?: string
  transactionId?: string
}) => {
  const resolverContext = useTransferResolverContext()

  const methods = useForm<TransferFormValues, TransferResolverContext>({
    mode: 'onBlur',
    resolver: transferFormResolver,
    context: resolverContext,
    defaultValues: {
      sender: defaultSender,
      recipients: defaultRecipients
        ? defaultRecipients.map((recipient) => ({
            receiver: {
              address: recipient.address,
              compliantPaymentRelationId: recipient.compliantPaymentRelationId,
            },
            amount: recipient.amount,
          }))
        : [],
      transferToken: defaultTransferToken
        ? {
            token: defaultTransferToken.token,
            amount: defaultTransferToken.amount,
            chainId: defaultTransferToken.chainId,
          }
        : undefined,
      calls: [],
      memo: defaultMemo,
      transactionId: transactionId,
      swapData: {
        slippage: bpsToPercentage(DEFAULT_SLIPPAGE_BPS).toString(),
      },
      offrampRecipientAmount: '',
      offrampBelowMinimum: false,
      offrampIsEurc: false,
    },
  })

  return methods
}

export const useSwapPreview = ({
  formData,
  isPrivate,
}: {
  formData: TransferFormValues
  isPrivate?: boolean
}) => {
  const isDirectionReversed = !!formData.isDirectionReversed

  const swapRequest: SwapRequest = useSwapRequest({
    account: formData.sender,
    sourceChainId: formData.swapData?.sellToken?.chainId,
    sourceToken: formData.swapData?.sellToken?.token,
    sourceAmount: undefined,
    destinationToken: formData.transferToken?.token,
    destinationChainId: formData.transferToken?.chainId,
    destinationAmount: formData.transferToken?.amount,
    swapDirection: SwapDirection.BUY,
    verifyBalance: true,
    isPrivate,
    receiver:
      formData.recipients && formData.recipients.length === 1
        ? formData.recipients[0]?.receiver?.address
        : undefined,
    slippage: formData.swapData?.slippage,
  })

  const debouncedQuery = useDebounce(swapRequest, 250)

  // Every quote failure here is surfaced inline on the payment-source field,
  // so always silence the polling quote's failure toast.
  const {
    data: swapQuote,
    isFetching,
    dataUpdatedAt,
    refetch,
    failureCount,
    failureReason,
  } = useSwapQuoteQuery(debouncedQuery, { silent: true })

  /**
   * We also calculate the reverse direction (SELL instead of BUY) in case its a better price and the user would like to switch.
   * This quote will not have an exact output amount.
   */
  const {
    data: reverseSwapQuote,
    isFetching: isFetchingReverseSwapQuote,
    dataUpdatedAt: dataUpdatedAtReverseSwapQuote,
    refetch: refetchReverseSwapQuote,
    failureCount: failureCountReverseSwapQuote,
    failureReason: failureReasonReverseSwapQuote,
  } = useReverseSwapQuoteQuery(debouncedQuery, swapQuote?.data)

  // When reversed, the "active" quote (the one used for the transaction) becomes the reversed (SELL) quote.
  return useMemo(() => {
    const quote = isDirectionReversed ? reverseSwapQuote : swapQuote
    return {
      swapQuote: quote,
      reverseSwapQuote: isDirectionReversed ? swapQuote : reverseSwapQuote,
      isValidKey:
        getSwapQuoteKey(swapRequest, isDirectionReversed) === quote?.queryKey,
      isLoading: isDirectionReversed ? isFetchingReverseSwapQuote : isFetching,
      dataUpdatedAt: isDirectionReversed
        ? dataUpdatedAtReverseSwapQuote
        : dataUpdatedAt,
      refetch: isDirectionReversed ? refetchReverseSwapQuote : refetch,
      failureCount: isDirectionReversed
        ? failureCountReverseSwapQuote
        : failureCount,
      failureReason: isDirectionReversed
        ? failureReasonReverseSwapQuote
        : failureReason,
      isDirectionReversed,
    }
  }, [
    isDirectionReversed,
    swapRequest,
    swapQuote,
    isFetching,
    dataUpdatedAt,
    refetch,
    failureCount,
    failureReason,
    reverseSwapQuote,
    isFetchingReverseSwapQuote,
    dataUpdatedAtReverseSwapQuote,
    refetchReverseSwapQuote,
    failureCountReverseSwapQuote,
    failureReasonReverseSwapQuote,
  ])
}

export const useCreateTransferTransactionSubmit = ({
  activeOrgId,
  transferTokenChainId,
  transferTokenAddress,
  sellTokenMetadata,
  transferTokenMetadata,
  isPrivate,
  setIsOpen,
}: {
  activeOrgId?: string
  transferTokenChainId: SupportedChainId
  transferTokenAddress: IAddress
  sellTokenMetadata?: TokenMetadata
  transferTokenMetadata?: TokenMetadata
  isPrivate?: boolean
  setIsOpen: (isOpen: boolean) => void
}) => {
  const queryClient = useQueryClient()

  const {
    createTransferTransaction,
    isProcessing: isProcessingTransferTransaction,
  } = useCreateTransferTransaction({
    token: transferTokenAddress,
    chainId: transferTokenChainId,
  })

  const { createSwapTransaction, isProcessing: isProcessingCreateSwap } =
    useCreateSwapTransaction()

  const createTransactionOnSubmit = useCallback(
    (
      dontOpenDialog: boolean,
      callback?: (transaction: OrgTransaction) => void,
    ) =>
      async ({
        sender,
        data,
        loggingData,
      }: {
        sender: OrgSmartAccount
        data: TransferFormValues
        loggingData?: BackendLoggingData
      }) => {
        const isSwap = !!data.swapData?.swapRouter
        const validatedRecipients = getValidatedRecipients(data.recipients)

        if (!isSwap) {
          await createTransferTransaction({
            transactionId: data.transactionId,
            data: {
              smartAccountAddress: sender.address,
              smartAccountId: sender.id,
              calls: data.calls,
              recipients: validatedRecipients.map((recipient) => ({
                address: recipient.receiver.address,
                amount: recipient.amount,
                compliantPaymentRelationId:
                  recipient.receiver.compliantPaymentRelationId,
              })),
              token: data.transferToken.token,
              memo: data.memo,
              chainId: data.transferToken.chainId,
            },
            dontOpenDialog,
            onSuccess: (transaction) => {
              queryClient.setQueryData(
                [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
                // Add isInitial to the transaction to trigger loading state
                { ...transaction, isInitial: true },
              )
              setIsOpen(false)
              callback?.(transaction)
            },
          })
        } else {
          if (!data.swapData?.sellToken) {
            throw new Error('Sell token is required')
          }
          if (!data.swapData?.swapRouter) {
            throw new Error('Swap router is required')
          }

          await createSwapTransaction({
            transactionId: data.transactionId,
            data: {
              smartAccountAddress: sender.address,
              smartAccountId: sender.id,
              sellAmount: parseUnits(
                data.swapData.sellToken.amount,
                sellTokenMetadata?.decimals ?? 18,
              ),
              receiver: validatedRecipients[0].receiver.address,
              routerType: data.swapData.swapRouter,
              calls: data.calls,
              sellToken: {
                address: data.swapData.sellToken.token,
                decimals: sellTokenMetadata?.decimals ?? 18,
                symbol: sellTokenMetadata?.symbol ?? '',
              },
              buyToken: {
                address: data.transferToken.token,
                decimals: transferTokenMetadata?.decimals ?? 18,
                symbol: transferTokenMetadata?.symbol ?? '',
              },
              buyAmount: parseUnits(
                data.transferToken.amount,
                transferTokenMetadata?.decimals ?? 18,
              ),
              chainId: data.swapData.sellToken.chainId,
              destinationChainId: data.transferToken.chainId,
              memo: data.memo,
              loggingData: loggingData,
              providerTransactionId: data.swapData.providerTransactionId,
              settlementRouter: data.swapData.settlementRouter,
              direction: SwapDirection.BUY,
              provider: data.swapData.swapRouter,
              private: isPrivate,
              earnData: data.swapData.earnToken
                ? {
                    amount: parseUnits(
                      data.swapData.earnToken.amount ?? '0',
                      data.swapData.earnToken.action === EarnAction.DEPOSIT
                        ? (transferTokenMetadata?.decimals ?? 18)
                        : (sellTokenMetadata?.decimals ?? 18),
                    ),
                    action: data.swapData.earnToken.action,
                    token: data.swapData.earnToken.token,
                    chainId: data.swapData.earnToken.chainId,
                  }
                : undefined,
              slippage:
                data.swapData.slippage ??
                bpsToPercentage(DEFAULT_SLIPPAGE_BPS).toString(), // We're just getting the worst-case slippage here, because if we computed a better case in the form it's filled into the swap data slippage (so we don't need to refetch the tokens)
              recipients: validatedRecipients.map((recipient) => ({
                address: recipient.receiver.address,
                amount: recipient.amount,
                compliantPaymentRelationId:
                  recipient.receiver.compliantPaymentRelationId,
              })),
            },
            dontOpenDialog,
            onSuccess: (transaction) => {
              queryClient.setQueryData(
                [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
                transaction,
              )
              setIsOpen(false)
              callback?.(transaction)
            },
          })
        }
      },
    [
      createTransferTransaction,
      queryClient,
      activeOrgId,
      createSwapTransaction,
      sellTokenMetadata?.decimals,
      sellTokenMetadata?.symbol,
      transferTokenMetadata?.decimals,
      transferTokenMetadata?.symbol,
      isPrivate,
      setIsOpen,
    ],
  )

  return {
    createTransactionOnSubmit,
    isProcessingTransaction:
      isProcessingTransferTransaction || isProcessingCreateSwap,
  }
}

export const useTokenAndChainFilter = ({
  isCompliantPayment,
  isMultiRecipient,
  isPrivate,
  isInternalTransfer,
  payee,
}: {
  isCompliantPayment?: boolean
  isMultiRecipient?: boolean
  isPrivate?: boolean
  isInternalTransfer?: boolean
  payee: { chains?: SupportedChainId[] } | undefined
}) => {
  const { hasFeature, tokenFeatures } = useTokenFeatures()
  const allowVaultTokens = Boolean(!isMultiRecipient && isInternalTransfer)

  const tokenFilter = useCallback(
    (token: { chainId: SupportedChainId; address: string }) => {
      // For private transfers the features endpoint drives which (chain,
      // token) pairs are supported — including relay chains, which
      // isTransactChainId would wrongly reject. Vault tokens are
      // source-only: privacy providers can't deliver vault shares, so they
      // never qualify as the token the recipient receives.
      if (isPrivate) {
        return (
          !isVaultToken(token.chainId, token.address) &&
          !!hasFeature(
            token.chainId,
            Address.format(token.address),
            'privateTransfers',
          )
        )
      }
      return (
        isTransactChainId(token.chainId) &&
        (allowVaultTokens
          ? true
          : !isVaultToken(token.chainId, EvmAddress.format(token.address)))
      )
    },
    [allowVaultTokens, hasFeature, isPrivate],
  )

  const chainFilter = useCallback(
    (chainId: SupportedChainId) => {
      const hasPrivateToken = !!tokenFeatures?.some(
        (t) => t.chainId === chainId && t.features.privateTransfers,
      )
      return (
        isTransactChainId(chainId) &&
        (!isPrivate || hasPrivateToken) &&
        (isCompliantPayment ? !!payee?.chains?.includes(chainId) : true)
      )
    },
    [isCompliantPayment, payee, isPrivate, tokenFeatures],
  )

  return { tokenFilter, chainFilter }
}

export const useIsInsufficientFunds = ({
  sender,
  token,
  chainId,
  amount,
}: {
  sender?: IAddress
  token?: IAddress
  chainId?: SupportedChainId
  amount?: string
}) => {
  const tokenMetadata = useTokenMetadata({
    address: token,
    chainId,
  })

  const amountBigInt = parseUnits(amount ?? '0', tokenMetadata?.decimals ?? 18)

  const { smartAccounts } = useSmartAccounts()

  const senderAccount = useMemo(
    () => smartAccounts.find((account) => account.address === sender),
    [smartAccounts, sender],
  )

  const { balances: balancesMap } = useMultipleSmartAccountBalances(
    senderAccount ? [senderAccount.address] : [],
    true,
  )

  const senderBalances = useMemo(
    () =>
      senderAccount ? (balancesMap[senderAccount.address]?.balances ?? []) : [],
    [balancesMap, senderAccount],
  )

  const senderBalance = senderBalances.find(
    (balance) =>
      balance.chainId === chainId && Address.equal(balance.address, token),
  )?.amount

  const isInsufficientFunds = useMemo(() => {
    if (
      sender &&
      token &&
      !Address.compatible(Address.format(sender), Address.format(token))
    ) {
      return true
    }

    return amountBigInt ? (senderBalance ?? 0n) < amountBigInt : false
  }, [sender, token, senderBalance, amountBigInt])

  return isInsufficientFunds
}

export const useIsInternalTransfer = ({
  recipientAddress,
}: {
  recipientAddress: IAddress | undefined
}) => {
  const { smartAccounts } = useSmartAccounts()

  const isInternalTransfer = useMemo(
    () => smartAccounts.some((account) => account.address === recipientAddress),
    [smartAccounts, recipientAddress],
  )

  return isInternalTransfer
}
